"use client";

import * as Sentry from "@sentry/nextjs";
import { useEffect } from "react";

/**
 * Global Error Boundary for Root Layout errors
 * This catches errors that occur at the root layout level
 * and reports them to Sentry
 */
export default function GlobalError({
	error,
	reset,
}: {
	error: Error & { digest?: string };
	reset: () => void;
}) {
	useEffect(() => {
		// Send error to Sentry
		Sentry.captureException(error);
	}, [error]);

	return (
		<html lang="en">
			<body className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
				<div className="max-w-md w-full text-center">
					<h1 className="text-6xl font-bold text-gray-900 mb-4">Oops!</h1>
					<h2 className="text-xl font-semibold text-gray-700 mb-4">
						Something went wrong
					</h2>
					<p className="text-gray-600 mb-8">
						We&apos;ve been notified and are working to fix this issue.
					</p>
					<div className="space-x-4">
						<button
							type="button"
							onClick={reset}
							className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
						>
							Try again
						</button>
						<a
							href="/"
							className="inline-flex items-center px-6 py-3 border border-gray-300 text-base font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
						>
							Go home
						</a>
					</div>
				</div>
			</body>
		</html>
	);
}
